import React, { useContext, useCallback } from 'react';
import { getEditorSettings } from 'components/property-editors/utils/EditorConfigurationUtils';
import './EditorEntityPicker.less';
import { IEditorProps } from '../Editor';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { useEpickEditor } from './useEntityPickerEditor';
import { EntityPickerWithLinks } from './EntityPickerWithLinks';

export interface IEntityPickerProps extends IEditorProps {
  getEntityLabel?: (value: object | string) => Promise<string>;
  canEdit?: boolean;
  canCreate?: boolean;
}

export interface EntityPickedInfo {
  id: string;
  name: string;
}

/**
 * Entity Picker Editor.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function EditorEntityPicker(props: IEntityPickerProps) {
  const { child, path, hideInputLabel } = props;
  const context = useContext(PropertyEditorContext);
  const { showEdit, showCreate, showPick, showReset, entityName, onValueChange } = useEpickEditor({
    canCreate: true,
    canEdit: true,
    ...props,
  });

  const showPicker = useCallback(() => {
    context.onShowDialogue({
      node: child,
      path,
      editingContextId: context.editingContextId,
      name: 'ShowEntityPicker',
      options: {
        onPicked: (value: EntityPickedInfo) => {
          onValueChange(value.id);
        },
        entityPickerSettings: getEditorSettings(child),
        onRefresh: () => {
          context.refreshContext();
        },
      },
    });
  }, [context, child, path, onValueChange]);

  const drop = useCallback(() => {
    onValueChange(null);
  }, [onValueChange]);

  const showEditor = useCallback(() => {
    context.onShowDialogue({
      node: child,
      path,
      editingContextId: context.editingContextId,
      name: 'ShowEditor',
    });
  }, [child, context, path]);

  const showCreator = useCallback(() => {
    context.onShowDialogue({
      node: child,
      path,
      editingContextId: context.editingContextId,
      name: 'ShowCreator',
      options: {
        onCreated: onValueChange,
      },
    });
  }, [child, context, onValueChange, path]);

  return (
    <EntityPickerWithLinks
      editor={child}
      path={path}
      hideInputLabel={hideInputLabel}
      onDrop={showReset ? drop : undefined}
      onCreateClick={showCreate ? showCreator : undefined}
      onPickClick={showPick ? showPicker : undefined}
      onEditClick={showEdit ? showEditor : undefined}
      entityName={entityName}
    />
  );
}
